// @flow

import React from 'react';
import type { SelectConvertorMap } from '../types/commonTypes';
import { defaultCurrency, defaultSurfaceUnit, dimensions, twCurrency, twSurfaceUnit } from '../const';
import { roundValue, formatMoneyValueWithSymbol, addCurrencySymbol, addDimensionSymbol } from './formatUtils';

export const getConvertedValueFromCurrentCurrency =
  (value: number, currenciesList: SelectConvertorMap, currency, digits?: number): string => {
    if (!currenciesList || !currenciesList[currency]) {
      return value;
    }
    const currentCurrency = currenciesList[currency];
    const convertedValue = parseFloat(value) * currentCurrency.convertorFactor;
    return convertedValue.toFixed(digits);
  };

export const undoConvertedValueFromCurrentCurrency =
  (value: number, currenciesList: SelectConvertorMap, currency): number => {
    if (!currenciesList || !currenciesList[currency]) {
      return value;
    }
    const currentCurrency = currenciesList[currency];
    return parseFloat(value) / currentCurrency.convertorFactor;
  };


export const currencyConvertor = (
  value: number | string,
  cookies: Cookies,
  currenciesList: SelectConvertorMap,
  quantity: string = '',
  digits?: number,
): string => {
  const numberValue = parseFloat(value);
  const currency = cookies.get(twCurrency) || defaultCurrency;
  if (!currenciesList[currency]) {
    return roundValue(numberValue, null, digits);
  }
  const roundedValue = roundValue(
    parseFloat(getConvertedValueFromCurrentCurrency(numberValue, currenciesList, currency, digits)),
    currency,
    digits,
  );
  return formatMoneyValueWithSymbol(
    roundedValue,
    currenciesList[currency].symbol,
    currency,
    quantity,
  );
};

export const getCurrencyBySurface = (
  value: number,
  gla: number,
  isCurrencyBySurface: boolean,
  surfaceUnit: string,
): string => {
  if (isCurrencyBySurface) {
    if (!gla || gla === 0) {
      return 'N/A';
    }
    return roundValue(parseFloat(value / gla), surfaceUnit);
  }
  return roundValue(parseFloat(value), surfaceUnit);
};

export const currencyBySurfaceConvertor = (
  value: number, cookies: Cookies, currenciesList: SelectConvertorMap,
  isCurrencyBySurface, gla = 1, withSymbol, quantity = '',
): string => {
  const surfaceUnit = cookies.get(twSurfaceUnit) || defaultSurfaceUnit;
  const currency = cookies.get(twCurrency) || defaultCurrency;

  if (currenciesList && currenciesList[currency] && dimensions[surfaceUnit]) {
    let currencyBySurface = getCurrencyBySurface(
      value * currenciesList[currency].convertorFactor,
      gla * dimensions[surfaceUnit].convertorFactor,
      isCurrencyBySurface,
      surfaceUnit,
    );
    if (withSymbol) {
      currencyBySurface = addCurrencySymbol(
        currencyBySurface,
        currenciesList[currency].symbol,
        currency,
      );
    }

    return `${currencyBySurface}${quantity} ${addDimensionSymbol(dimensions[surfaceUnit].symbol, isCurrencyBySurface)}`;
  }

  return `${getCurrencyBySurface(value, gla, isCurrencyBySurface, surfaceUnit)} ?`;
};


export const surfaceConvertor = (
  value: number | string,
  cookies: Cookies,
): string => {
  const surfaceUnit = cookies.get(twSurfaceUnit) || defaultSurfaceUnit;
  if (!dimensions[surfaceUnit]) {
    return `${roundValue(value)} ?`;
  }
  const currentDimension = dimensions[surfaceUnit];
  const convertedValue = roundValue(
    parseFloat(value * currentDimension.convertorFactor),
    surfaceUnit,
  );

  return `${convertedValue} ${currentDimension.symbol}`;
};
